import { logger } from '@/ui'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import type { CandidateEventResponse } from '@jobilla/nexus-sdk'
import { format, formatDistanceToNow, isSameDay } from 'date-fns'
import { CalendarIcon, CheckIcon, ClockIcon, LinkIcon, MoreVerticalIcon, VideoIcon } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// Within this long of the join window opening, tick a live second-by-second countdown;
// beyond it, show a coarse relative label and just wake up once when it enters range.
export const LIVE_COUNTDOWN_WITHIN_MS = 60 * 60 * 1000

type SdkEventBooking = NonNullable<CandidateEventResponse['booking']>

export interface InterviewMeeting {
	type: SdkEventBooking['meeting']['type']
	startsAt: Date
	endsAt: Date
	joinableAt: Date
	expiresAt: Date
	candidateLink: string | null
	recruiterLink: string | null
}

export interface EventBooking {
	id: string
	status: SdkEventBooking['status']
	candidateAttended: boolean | null
	meeting: InterviewMeeting
}

/** Maps the SDK's snake_case booking (attached to booking events) to the camelCase
 * shape the meeting card renders, converting ISO strings to Dates. */
export function mapEventBooking(booking: SdkEventBooking): EventBooking {
	return {
		id: booking.id,
		status: booking.status,
		candidateAttended: booking.candidateAttended,
		meeting: {
			type: booking.meeting.type,
			startsAt: new Date(booking.meeting.startsAt),
			endsAt: new Date(booking.meeting.endsAt),
			joinableAt: new Date(booking.meeting.joinableAt),
			expiresAt: new Date(booking.meeting.expiresAt),
			candidateLink: booking.meeting.candidateLink,
			recruiterLink: booking.meeting.recruiterLink,
		},
	}
}

/**
 * A `now` that re-renders as it approaches `target`: every second once within
 * `liveWithinMs` of it (so a seconds countdown ticks and the join CTA flips the
 * instant the room opens), plus a single wake-up when it first enters that window.
 * No timers run when `target` is null or already past.
 */
export function useApproachingNow(target: Date | null, liveWithinMs: number): Date {
	const [now, setNow] = useState(() => new Date())

	useEffect(() => {
		if (target === null) {
			return
		}
		const remaining = target.getTime() - now.getTime()
		if (remaining <= 0) {
			return
		}
		// setTimeout overflows (fires immediately, looping) past ~24.8 days, so cap the
		// sleep-until-near-the-window delay; a far-future meeting just re-checks then.
		const delay = remaining > liveWithinMs
			? Math.min(remaining - liveWithinMs, 2_147_483_647)
			: 1000
		const timer = setTimeout(() => setNow(new Date()), delay)

		return () => clearTimeout(timer)
	}, [target, now, liveWithinMs])

	return now
}

function formatJoinCountdown(remainingMs: number): string {
	const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000))
	const minutes = Math.floor(totalSeconds / 60)
	const seconds = totalSeconds % 60

	return `${minutes}:${String(seconds).padStart(2, '0')}`
}

const JOIN_CONTROL_CLASS =
	'inline-flex cursor-pointer items-center gap-1.5 justify-center rounded-md border border-grey-200 bg-white px-2.5 py-1 text-xs font-medium text-grey-700 transition-colors hover:border-brand-500 hover:text-brand-700'

interface MeetingCardProps {
	meeting: InterviewMeeting
	className?: string
	/** Show the attendance control (a confirmed interview whose time has passed). */
	showAttendance?: boolean
	/** Recorded attendance: true = attended, false = no-show, null = not yet recorded. */
	candidateAttended?: boolean | null
	isSavingAttendance?: boolean
	onSetAttendance?: (attended: boolean) => void
	/** Jobilla Meet only: open the in-app call tab instead of the external DS page. */
	onJoinInApp?: () => void
}

/**
 * The interview meeting panel: location + schedule window + a live join CTA that counts
 * down to the room opening and flips to the real button in place. Shared by the interviews
 * timeline and the candidate overview's "interview today" panel.
 */
export function MeetingCard({
	meeting,
	className,
	showAttendance = false,
	candidateAttended = null,
	isSavingAttendance = false,
	onSetAttendance,
	onJoinInApp,
}: MeetingCardProps) {
	const { t } = useTranslation('common')
	const candidateLink = meeting.candidateLink
	const [candidateLinkCopied, setCandidateLinkCopied] = useState(false)
	const handleCopyCandidateLink = useCallback(async () => {
		if (candidateLink === null) {
			return
		}
		try {
			await navigator.clipboard.writeText(candidateLink)
			setCandidateLinkCopied(true)
			setTimeout(() => setCandidateLinkCopied(false), 2000)
		} catch (error) {
			logger.error(error, { tags: { component: 'MeetingCard', action: 'copyCandidateLink' } })
		}
	}, [candidateLink])
	const room = meeting.recruiterLink
	// The join CTA only shows inside the window (joinable_at ≤ now < deadline); before/after we
	// surface why instead. For Jobilla Meet the deadline is the meeting's end — the DS session is
	// over past it, so the token's extra grace can't actually be used. Other providers keep the
	// grace (their link may still work if the meeting runs over). `now` ticks each second near
	// joinable_at so the countdown runs live and the CTA flips in place — no reload needed.
	const joinDeadline = meeting.type === 'jobilla_meet' ? meeting.endsAt : meeting.expiresAt
	// Track whichever boundary is next: count down to joinable_at while it's still ahead, then
	// switch to the deadline so the CTA also flips to the "ended" notice in place. Targeting only
	// joinable_at froze `now` the moment the room opened, leaving a stale join button past the end.
	const nextBoundary = room === null
		? null
		: Date.now() < meeting.joinableAt.getTime()
		? meeting.joinableAt
		: joinDeadline
	const now = useApproachingNow(nextBoundary, LIVE_COUNTDOWN_WITHIN_MS)
	let joinLink: string | null = null
	let joinNotice: { text: string; upcoming: boolean } | null = null
	if (room !== null) {
		if (now < meeting.joinableAt) {
			const untilJoinableMs = meeting.joinableAt.getTime() - now.getTime()
			joinNotice = {
				// Within the live window, a locale-neutral M:SS countdown via a dedicated key so the
				// preposition is translated rather than a hard-coded English "in"; further out, a
				// coarse relative phrase.
				text: untilJoinableMs <= LIVE_COUNTDOWN_WITHIN_MS
					? t('interviews.timeline.joinableInCountdown', {
						countdown: formatJoinCountdown(untilJoinableMs),
					})
					: t('interviews.timeline.joinableIn', {
						time: formatDistanceToNow(meeting.joinableAt, { addSuffix: true }),
					}),
				upcoming: true,
			}
		} else if (now >= joinDeadline) {
			joinNotice = {
				text: t('interviews.timeline.endedAgo', {
					time: formatDistanceToNow(meeting.endsAt, { addSuffix: true }),
				}),
				upcoming: false,
			}
		} else {
			joinLink = room
		}
	}

	return (
		<div
			className={['flex flex-col gap-2 rounded-lg border border-grey-200 bg-grey-50 p-3', className]
				.filter(Boolean)
				.join(' ')}
		>
			<div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
				<span className="inline-flex items-center gap-1 rounded-full border border-brand-100 bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
					<VideoIcon className="size-3" aria-hidden="true" />
					{t(`sendBookingLinkDialog.location.${meeting.type}`)}
				</span>
				{candidateLink !== null
					? (
						<Menu as="div" className="relative">
							<MenuButton
								className="cursor-pointer rounded-md p-1 text-grey-400 transition-colors hover:bg-grey-100 hover:text-grey-600"
								aria-label={t('interviews.timeline.candidateMeetingLink')}
							>
								<MoreVerticalIcon className="size-3.5" aria-hidden="true" />
							</MenuButton>
							<MenuItems className="absolute right-0 z-50 mt-1 w-max min-w-[12rem] max-w-[16rem] rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5">
								<MenuItem>
									<button
										type="button"
										onClick={(event) => {
											// Keep the menu open so the "Copied" confirmation is visible; Headless UI
											// otherwise closes it on item click and the feedback never renders.
											event.preventDefault()
											void handleCopyCandidateLink()
										}}
										className="flex w-full cursor-pointer items-center gap-2 whitespace-nowrap px-3 py-2 text-sm text-grey-700 hover:bg-grey-100"
									>
										{candidateLinkCopied
											? (
												<>
													<CheckIcon className="size-4 text-green-500" aria-hidden="true" />
													{t('interviews.copied')}
												</>
											)
											: (
												<>
													<LinkIcon className="size-4" aria-hidden="true" />
													{t('interviews.timeline.candidateMeetingLink')}
												</>
											)}
									</button>
								</MenuItem>
							</MenuItems>
						</Menu>
					)
					: null}
			</div>
			<span className="inline-flex items-center gap-1.5 text-xs font-medium text-grey-700">
				<CalendarIcon className="size-3.5 text-grey-400" aria-hidden="true" />
				{`${format(meeting.startsAt, 'PPp')} – ${
					format(meeting.endsAt, isSameDay(meeting.startsAt, meeting.endsAt) ? 'p' : 'PPp')
				}`}
			</span>
			{joinLink
				? (
					meeting.type === 'jobilla_meet' && onJoinInApp
						? (
							<button type="button" onClick={onJoinInApp} className={JOIN_CONTROL_CLASS}>
								<VideoIcon className="size-3.5" aria-hidden="true" />
								{t('interviews.timeline.joinMeeting')}
							</button>
						)
						: (
							<a href={joinLink} target="_blank" rel="noopener noreferrer" className={JOIN_CONTROL_CLASS}>
								<VideoIcon className="size-3.5" aria-hidden="true" />
								{t('interviews.timeline.joinMeeting')}
							</a>
						)
				)
				: joinNotice
				? (
					<span
						className={`inline-flex items-center gap-1.5 justify-center rounded-md border border-grey-200 bg-white px-2.5 py-1 text-xs font-medium ${
							joinNotice.upcoming ? 'text-grey-500' : 'text-grey-400'
						}`}
					>
						{joinNotice.upcoming ? <ClockIcon className="size-3.5" aria-hidden="true" /> : null}
						{joinNotice.text}
					</span>
				)
				: null}
			{showAttendance
				? (
					<div className="-mx-3 border-t border-grey-100 px-3 pt-2.5">
						<div className="flex items-center justify-between gap-2">
							<span className="text-xs text-grey-400">
								{t('interviews.attendance.prompt')}
							</span>
							<div className="flex items-center gap-1.5">
								<button
									type="button"
									onClick={() => onSetAttendance?.(true)}
									disabled={isSavingAttendance}
									className={`cursor-pointer rounded-md border px-2 py-0.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
										candidateAttended === true
											? 'border-green-600 bg-green-50 text-green-700'
											: 'border-grey-200 text-grey-500 hover:border-green-500 hover:text-green-700'
									}`}
								>
									{t('interviews.attendance.yes')}
								</button>
								<button
									type="button"
									onClick={() => onSetAttendance?.(false)}
									disabled={isSavingAttendance}
									className={`cursor-pointer rounded-md border px-2 py-0.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
										candidateAttended === false
											? 'border-red-600 bg-red-50 text-red-700'
											: 'border-grey-200 text-grey-500 hover:border-red-500 hover:text-red-700'
									}`}
								>
									{t('interviews.attendance.no')}
								</button>
							</div>
						</div>
					</div>
				)
				: null}
		</div>
	)
}
